/**
 * ReportStatusCard Component
 * Phase 7: Displays report generation status with download action.
 */

import { ReportStatusResponse, ReportFormat } from '../lib/api'
import { StatusBadge } from './StatusBadge'
import { useLocale } from '../contexts/LocaleContext'

export interface ReportStatusCardProps {
  /** Report status from backend */
  report: ReportStatusResponse
  
  /** Download handler */
  onDownload: (reportId: string) => void
  
  /** Refresh handler for pending/generating reports */
  onRefresh?: (reportId: string) => void
}

function getFormatIcon(format: ReportFormat): string {
  switch (format) {
    case 'html':
      return '📄'
    case 'pdf':
      return '📕'
    case 'json':
      return '📊'
    default:
      return '📁'
  }
}

function getFormatLabel(format: ReportFormat, t: (key: string) => string): string {
  const formatMap: Record<ReportFormat, string> = {
    html: t('reports.htmlCard'),
    pdf: t('reports.pdfCard'),
    json: t('reports.jsonCard'),
  }
  return formatMap[format] || format.toUpperCase()
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

function formatTimestamp(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleString()
}

export function ReportStatusCard({
  report,
  onDownload,
  onRefresh,
}: ReportStatusCardProps) {
  const { t } = useLocale()
  const formatLabel = getFormatLabel(report.format, t)
  const canDownload = report.status === 'completed'
  const isProcessing = report.status === 'pending' || report.status === 'generating'
  
  return (
    <div className="card" data-testid="report-status-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg" aria-hidden="true">
              {getFormatIcon(report.format)}
            </span>
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              {formatLabel}
            </h3>
          </div>
          
          <div className="mb-3">
            <StatusBadge status={report.status} />
          </div>
          
          <div className="space-y-1 text-xs text-[var(--text-secondary)]">
            <div>
              <span className="text-[var(--text-tertiary)]">{t('reports.createdAt')}</span>{' '}
              {formatTimestamp(report.created_at)}
            </div>
            
            {report.generated_at && (
              <div>
                <span className="text-[var(--text-tertiary)]">{t('reports.generatedAt')}</span>{' '}
                {formatTimestamp(report.generated_at)}
              </div>
            )}
            
            {report.file_size_bytes !== null && (
              <div>
                <span className="text-[var(--text-tertiary)]">{t('reports.size')}</span>{' '}
                {formatBytes(report.file_size_bytes)}
              </div>
            )}
            
            {report.error_message && (
              <div className="text-[var(--severity-critical)] mt-2">
                <span className="font-medium">{t('common.error')}:</span> {report.error_message}
              </div>
            )}
            {isProcessing && (
              <div className="audit-progress-track report-progress-bar" role="progressbar" aria-label={t('reports.building')}>
                <div className="audit-progress-seg audit-progress-seg-run" style={{ width: '45%' }} />
                <div className="audit-progress-seg audit-progress-seg-wait" style={{ width: '55%' }} />
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          {canDownload && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => onDownload(report.report_id)}
              aria-label={t('reports.downloadAria', { label: formatLabel })}
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {t('common.download')}
            </button>
          )}
          
          {isProcessing && onRefresh && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => onRefresh(report.report_id)}
              aria-label={t('reports.refreshAria')}
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t('common.refresh')}
            </button>
          )}
          
          {report.status === 'failed' && onRefresh && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => onRefresh(report.report_id)}
              aria-label={t('reports.checkStatusAria')}
            >
              {t('common.retry')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
